/**
 * Centralized locale-aware formatting for dates, times, numbers, and currencies.
 *
 * All user-facing timestamps, numbers, and monetary values MUST be rendered
 * through these functions so output matches the viewer's locale and timezone.
 *
 * Datetime functions use dayjs (already a project dependency) with the `utc`
 * and `timezone` plugins so UTC values stored in the database are correctly
 * shifted to the viewer's local timezone before display.
 *
 * Number and currency functions use the native Intl.NumberFormat API —
 * zero bundle cost, full locale/currency coverage in all modern browsers.
 */

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Browser locale string (e.g. 'en-US', 'de-DE'). Falls back in Jest/SSR. */
export const DISPLAY_LOCALE: string =
  typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

/** IANA timezone from the browser (e.g. 'America/Chicago'). Falls back to UTC. */
export const DISPLAY_TIMEZONE: string =
  typeof Intl !== 'undefined' ? (Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC') : 'UTC';

// ── Date / time ───────────────────────────────────────────────────────────────

/**
 * Format a UTC ISO string as a short date in the viewer's locale and timezone.
 *
 * @example
 * formatDate('2026-09-15T14:00:00Z') // → "Sep 15, 2026" (en-US)
 * formatDate('2026-09-15T14:00:00Z') // → "15 sept. 2026" (fr-FR)
 */
export const formatDate = (
  isoString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!isoString) {
    return '—';
  }
  const d = dayjs.utc(isoString).tz(DISPLAY_TIMEZONE).toDate();
  if (Number.isNaN(d.getTime())) {
    return isoString;
  }
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(d);
};

/**
 * Format a UTC ISO string as date + time in the viewer's locale and timezone.
 *
 * @example
 * formatDateTime('2026-09-15T14:00:00Z') // → "Sep 15, 2026, 9:00 AM" (en-US, CT)
 */
export const formatDateTime = (
  isoString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!isoString) {
    return '—';
  }
  const d = dayjs.utc(isoString).tz(DISPLAY_TIMEZONE).toDate();
  if (Number.isNaN(d.getTime())) {
    return isoString;
  }
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  }).format(d);
};

/**
 * Format a UTC ISO string as a locale-aware relative time string.
 * Uses Intl.RelativeTimeFormat so the output matches the viewer's language.
 *
 * @example
 * formatRelativeTime('2026-09-15T12:00:00Z') // → "2 hours ago" (en-US)
 * formatRelativeTime('2026-09-15T12:00:00Z') // → "vor 2 Stunden" (de-DE)
 */
export const formatRelativeTime = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '—';
  }
  const d = dayjs.utc(isoString).tz(DISPLAY_TIMEZONE);
  if (!d.isValid()) {
    return isoString;
  }

  const diffSeconds = d.diff(dayjs(), 'second');
  const abs = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat(DISPLAY_LOCALE, { numeric: 'auto' });

  if (abs < 60) {
    return rtf.format(Math.round(diffSeconds), 'second');
  }
  if (abs < 3_600) {
    return rtf.format(Math.round(diffSeconds / 60), 'minute');
  }
  if (abs < 86_400) {
    return rtf.format(Math.round(diffSeconds / 3_600), 'hour');
  }
  if (abs < 2_592_000) {
    return rtf.format(Math.round(diffSeconds / 86_400), 'day');
  }
  if (abs < 31_536_000) {
    return rtf.format(Math.round(diffSeconds / 2_592_000), 'month');
  }
  return rtf.format(Math.round(diffSeconds / 31_536_000), 'year');
};

// ── Numbers ───────────────────────────────────────────────────────────────────

/**
 * Format a number in the viewer's locale.
 *
 * @example
 * formatNumber(1234567.89)       // → "1,234,567.89" (en-US)
 * formatNumber(1234567.89)       // → "1.234.567,89" (de-DE)
 * formatNumber(0.5, { style: 'percent' }) // → "50%" (en-US)
 */
export const formatNumber = (
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(DISPLAY_LOCALE, options).format(value);
};

/**
 * Format a number as a currency amount in the viewer's locale.
 * Defaults to USD; pass `currency` (ISO 4217 code) to override.
 *
 * @example
 * formatCurrency(1234.5)            // → "$1,234.50" (en-US / USD)
 * formatCurrency(1234.5, 'EUR')     // → "€1,234.50" (en-US / EUR)
 * formatCurrency(1234.5, 'EUR')     // → "1.234,50 €" (de-DE / EUR)
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency = 'USD',
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(DISPLAY_LOCALE, {
    style: 'currency',
    currency,
    ...options,
  }).format(value);
};

/**
 * Format a decimal fraction (0–1) as a percentage in the viewer's locale.
 *
 * @example
 * formatPercent(0.856)  // → "85.6%" (en-US)
 * formatPercent(0.856)  // → "85,6 %" (fr-FR)
 */
export const formatPercent = (
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(DISPLAY_LOCALE, {
    style: 'percent',
    maximumFractionDigits: 1,
    ...options,
  }).format(value);
};
